import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import axios from 'axios';
import { Plus, Pencil, Trash2, Building2, Phone, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = [
  { key: 'mon', label: 'Pzt' },
  { key: 'tue', label: 'Sal' },
  { key: 'wed', label: 'Çar' },
  { key: 'thu', label: 'Per' },
  { key: 'fri', label: 'Cum' },
  { key: 'sat', label: 'Cmt' },
  { key: 'sun', label: 'Paz' },
];

const emptyHours = () => DAYS.reduce((acc, d) => {
  acc[d.key] = { open: '09:00', close: '18:00', closed: d.key === 'sun' };
  return acc;
}, {});

export const AdminBranches = () => {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const [branches, setBranches] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '', working_hours: emptyHours() });
  const isStaff = user?.role === 'staff';
  const allowBranches = user?.allow_branches === true;

  useEffect(() => { if (allowBranches) fetchAll(); }, [allowBranches]);

  // Gate: Mini plan (allow_branches=false) cannot access this page
  if (user && !allowBranches) {
    return <Navigate to="/admin/plan-upgrade" replace />;
  }

  const fetchAll = async () => {
    try {
      const [bRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/branches`),
        axios.get(`${API_URL}/api/plan/info`),
      ]);
      setBranches(bRes.data);
      setPlanInfo(pRes.data);
    } catch {
      toast.error('Şubeler yüklenemedi');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', address: '', phone: '', working_hours: emptyHours() });
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || '',
      address: b.address || '',
      phone: b.phone || '',
      working_hours: { ...emptyHours(), ...(b.working_hours || {}) },
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: form.name, address: form.address, phone: form.phone, working_hours: form.working_hours };
      if (editing) {
        await axios.put(`${API_URL}/api/branches/${editing.id}`, payload);
        toast.success('Şube güncellendi');
      } else {
        await axios.post(`${API_URL}/api/branches`, payload);
        toast.success('Şube oluşturuldu');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    }
  };

  const del = async (b) => {
    if (!window.confirm(`"${b.name}" şubesini silmek istediğinize emin misiniz? Bu şubeye bağlı randevular şubesiz kalacak.`)) return;
    try {
      await axios.delete(`${API_URL}/api/branches/${b.id}`);
      toast.success('Şube silindi');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const setDayField = (dayKey, field, value) => {
    setForm(prev => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [dayKey]: { ...(prev.working_hours[dayKey] || {}), [field]: value }
      }
    }));
  };

  const maxBranches = planInfo?.limits?.max_branches;
  const currentCount = planInfo?.usage?.branches || 0;
  const atLimit = maxBranches !== null && maxBranches !== undefined && currentCount >= maxBranches;

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="branches-title">Şubeler</h1>
            <p className="text-base text-[#595959] mt-2">
              {planInfo ? (
                <>Toplam: <strong>{currentCount}</strong> / {maxBranches === null || maxBranches === undefined ? '∞' : maxBranches} şube</>
              ) : 'Yükleniyor...'}
            </p>
          </div>
          {!isStaff && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={openCreate}
                  disabled={atLimit}
                  className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white disabled:opacity-50"
                  data-testid="add-branch-button"
                  title={atLimit ? 'Şube limiti doldu. Planınızı yükseltin.' : ''}
                >
                  <Plus className="w-5 h-5 mr-2" /> Şube Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="branch-dialog">
                <DialogHeader>
                  <DialogTitle>{editing ? 'Şubeyi Düzenle' : 'Yeni Şube'}</DialogTitle>
                  <DialogDescription>Şube bilgilerini girin</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label>Şube Adı *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="branch-name-input" />
                  </div>
                  <div>
                    <Label>Adres</Label>
                    <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="branch-address-input" />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="branch-phone-input" placeholder="05XX XXX XX XX" />
                  </div>
                  <div className="pt-2">
                    <Label className="mb-2 block">Çalışma Saatleri</Label>
                    <div className="space-y-2 border border-[#E5E5E5] rounded-xl p-3">
                      {DAYS.map(d => {
                        const day = form.working_hours[d.key] || {};
                        return (
                          <div key={d.key} className="flex items-center gap-2 text-sm" data-testid={`wh-row-${d.key}`}>
                            <span className="w-10 font-medium text-[#1A1A2E]">{d.label}</span>
                            <label className="flex items-center gap-1 text-xs text-[#595959]">
                              <input
                                type="checkbox"
                                checked={!day.closed}
                                onChange={(e) => setDayField(d.key, 'closed', !e.target.checked)}
                                data-testid={`wh-open-${d.key}`}
                              />
                              Açık
                            </label>
                            <Input
                              type="time"
                              value={day.open || '09:00'}
                              onChange={(e) => setDayField(d.key, 'open', e.target.value)}
                              disabled={day.closed}
                              className="h-8 text-xs w-28"
                            />
                            <span className="text-xs text-[#999]">→</span>
                            <Input
                              type="time"
                              value={day.close || '18:00'}
                              onChange={(e) => setDayField(d.key, 'close', e.target.value)}
                              disabled={day.closed}
                              className="h-8 text-xs w-28"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <Button type="submit" className="w-full" data-testid="branch-submit-button">
                    {editing ? 'Güncelle' : 'Oluştur'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {atLimit && !isStaff && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="branch-limit-warn">
            Şube limitine ulaştınız ({maxBranches}). Daha fazla şube eklemek için planınızı yükseltin.
          </div>
        )}

        {branches.length === 0 ? (
          <Card className="border border-dashed border-[#E5E5E5]">
            <CardContent className="p-10 text-center text-[#595959]" data-testid="no-branches">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-[#999]" strokeWidth={1.5} />
              Henüz şube eklenmemiş.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map(b => (
              <Card key={b.id} className="border border-[#E5E5E5]" data-testid={`branch-card-${b.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="p-2 rounded-lg bg-[#2563EB]/10 shrink-0">
                        <Building2 className="w-5 h-5 text-[#2563EB]" />
                      </div>
                      <h3 className="font-semibold text-[#1A1A2E] truncate" data-testid={`branch-name-${b.id}`}>{b.name}</h3>
                    </div>
                    {!isStaff && (
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => del(b)} data-testid={`delete-branch-${b.id}`}>
                          <Trash2 className="w-4 h-4 text-[#DC2626]" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {b.address && (
                    <div className="flex items-start gap-2 text-sm text-[#595959] mb-1.5">
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                      <span className="break-words">{b.address}</span>
                    </div>
                  )}
                  {b.phone && (
                    <div className="flex items-center gap-2 text-sm text-[#595959] mb-1.5">
                      <Phone className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                      <span>{b.phone}</span>
                    </div>
                  )}
                  {b.working_hours && Object.keys(b.working_hours).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-[#595959] mb-1.5">
                        <Clock className="w-3.5 h-3.5" /> Çalışma Saatleri
                      </div>
                      <div className="space-y-0.5">
                        {DAYS.map(d => {
                          const wh = b.working_hours[d.key];
                          if (!wh) return null;
                          return (
                            <div key={d.key} className="flex justify-between text-xs text-[#595959]">
                              <span className="w-8">{d.label}</span>
                              <span>{wh.closed ? 'Kapalı' : `${wh.open} - ${wh.close}`}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
};
